// ============================================
// DEMO TOUR — S72: Tour guiado para tenants demo
// ============================================
// Se activa cuando user.cliente.esDemo === true
// Guía al prospecto por las pantallas clave del sistema
// Guarda progreso en localStorage para retomar si cierra

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// ============================================
// PASOS DEL TOUR
// ============================================
const TOUR_STEPS = [
  {
    route: '/dashboard',
    icon: '📊',
    titulo: 'Dashboard',
    descripcion: 'De un vistazo: cuántas consultas entraron, cuántas ventas cerraste, cuánta plata te deben. Sin abrir Excel, sin preguntar al vendedor, sin revisar WhatsApp uno por uno.',
  },
  {
    route: '/chat',
    icon: '💬',
    titulo: 'Chat Unificado',
    descripcion: 'Cada consulta que te llega por WhatsApp e Instagram cae acá — no se pierde ninguna. El bot responde automático fuera de horario (esas consultas de las 11 de la noche que hoy nadie contesta) y cuando el vendedor abre el chat, ya tiene todo el contexto sin preguntar \'de dónde sos\' de nuevo.',
  },
  {
    route: '/pipeline',
    icon: '📋',
    titulo: 'Pipeline de Ventas',
    descripcion: '¿Cuántas consultas tenés sin contestar? ¿Cuántos presupuestos mandaste sin hacer seguimiento? Acá lo ves todo en columnas. Sabés quién se está enfriando y a quién llamar HOY. El orden es la diferencia entre vender 7 piletas y vender 17. Cada lead avanza por etapas: Nuevo → Calificado → Contactado → Negociación → Ganado.',
  },
  {
    route: '/sales',
    icon: '💰',
    titulo: 'Gestión de Ventas',
    descripcion: 'Cada venta queda registrada con todos los detalles: pileta, accesorios, instalación, cuotas. Tenés el historial completo de cada operación y el estado de cada cobro al instante, sin depender de planillas ni de la memoria de nadie.',
  },
  {
    route: '/payments',
    icon: '🏦',
    titulo: 'Control de Pagos',
    descripcion: '¿Quién te debe plata? ¿Cuánto? ¿Desde cuándo? Cuando un cliente se atrasa, lo ves al instante. Sin planillas de Excel ni depender de la memoria.',
  },
  {
    route: '/service-alerts',
    icon: '🔔',
    titulo: 'Alertas de Servicios',
    descripcion: 'Un cliente te compró una pileta hace un año. ¿Le ofreciste el service de bomba? ¿El cobertor antes del invierno? BiLeads te avisa automáticamente. Es plata que hoy se te escapa porque nadie se acuerda. Mejor anticiparse a los problemas que tener reclamos porque algo se rompió. Todas las alertas son 100% personalizables para tu negocio.',
  },
  {
    route: '/dashboard-ejecutivo',
    icon: '🎯',
    titulo: 'Centro de Mando',
    descripcion: 'Los números que le importan al dueño: cuánto se vendió, cuánto se cobró, qué vendedor rinde más, y si la publicidad está dando resultado o quemando plata.',
  },
  {
    route: '/analisis-leads',
    icon: '📈',
    titulo: 'Análisis de Leads',
    descripcion: 'De cada 100 consultas, ¿cuántas llegan a presupuesto? ¿Cuántas cierran? ¿Dónde se caen? Este embudo te muestra exactamente dónde se te escapan las ventas y es lo primero que arreglamos.',
  },
  {
    route: '/informes',
    icon: '📑',
    titulo: 'Informes',
    descripcion: 'Control financiero real: qué plata entró, de quién, por qué medio, y a qué caja. Cuenta corriente general de toda tu empresa. Para que cuando te sientes a fin de mes, los números ya estén. TIP: toca "mes" o "año" y mirá más información.',
  },
];

// ============================================
// COMPONENTE PRINCIPAL
// ============================================
export default function DemoTour() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const esDemo = user?.cliente?.esDemo || false;
  const clienteId = user?.clienteId || 'unknown';
  const storageKey = `demo_tour_${clienteId}`;

  // Estado del tour
  const [visible, setVisible] = useState(false);
  const [pasoActual, setPasoActual] = useState(0);
  const [tourCompletado, setTourCompletado] = useState(false);
  const [mostrarBienvenida, setMostrarBienvenida] = useState(false);
  const [mostrarCasoExito, setMostrarCasoExito] = useState(false);

  // Inicializar tour al montar
  useEffect(() => {
    if (!esDemo) return;

    const saved = localStorage.getItem(storageKey);
    if (saved === 'done') {
      setTourCompletado(true);
      return;
    }

    if (saved) {
      // Retomar donde quedó
      const pasoGuardado = parseInt(saved, 10);
      if (pasoGuardado >= 0 && pasoGuardado < TOUR_STEPS.length) {
        setPasoActual(pasoGuardado);
        setVisible(true);
        // Navegar a la ruta del paso guardado
        if (location.pathname !== TOUR_STEPS[pasoGuardado].route) {
          navigate(TOUR_STEPS[pasoGuardado].route);
        }
      }
    } else {
      // Primera vez — mostrar bienvenida
      setMostrarBienvenida(true);
    }
  }, [esDemo]);

  // No renderizar nada si no es demo o ya completó
  if (!esDemo || tourCompletado) return null;

  // ============================================
  // HANDLERS
  // ============================================
  // Bienvenida → Caso Éxito
  const mostrarCaso = () => {
    setMostrarBienvenida(false);
    setMostrarCasoExito(true);
  };

  // Caso Éxito → Tour paso a paso
  const iniciarTour = () => {
    setMostrarCasoExito(false);
    setPasoActual(0);
    setVisible(true);
    localStorage.setItem(storageKey, '0');
    if (location.pathname !== TOUR_STEPS[0].route) {
      navigate(TOUR_STEPS[0].route);
    }
  };

  const siguientePaso = () => {
    const nuevo = pasoActual + 1;
    if (nuevo >= TOUR_STEPS.length) {
      // Tour completado
      completarTour();
      return;
    }
    setPasoActual(nuevo);
    localStorage.setItem(storageKey, String(nuevo));
    navigate(TOUR_STEPS[nuevo].route);
  };

  const pasoAnterior = () => {
    const nuevo = Math.max(0, pasoActual - 1);
    setPasoActual(nuevo);
    localStorage.setItem(storageKey, String(nuevo));
    navigate(TOUR_STEPS[nuevo].route);
  };

  const completarTour = () => {
    localStorage.setItem(storageKey, 'done');
    setTourCompletado(true);
    setVisible(false);
  };

  const saltarTour = () => {
    completarTour();
  };

  // ============================================
  // PANTALLA DE BIENVENIDA
  // ============================================
  if (mostrarBienvenida) {
    return (
      <div style={styles.overlay}>
        <div style={styles.welcomeCard}>
          <div style={styles.welcomeIcon}>🏊</div>
          <h2 style={styles.welcomeTitle}>¡Bienvenido a BiLeads!</h2>
          <p style={styles.welcomeText}>
            Este es un sistema real con datos de ejemplo para que veas cómo funciona.
            Te vamos a guiar por las pantallas principales en menos de 2 minutos.
          </p>
          <p style={styles.welcomeSubtext}>
            Después del recorrido podés explorar libremente y tocar lo que quieras.
          </p>
          <div style={styles.welcomeButtons}>
            <button onClick={mostrarCaso} style={styles.btnPrimary}>
              Empezar recorrido →
            </button>
            <button onClick={saltarTour} style={styles.btnSkip}>
              Explorar por mi cuenta
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // PANTALLA CASO ÉXITO (paso intermedio)
  // ============================================
  if (mostrarCasoExito) {
    return (
      <div style={styles.overlay}>
        <div style={styles.casoCard}>
          <div style={styles.welcomeIcon}>🏊‍♂️</div>
          <h2 style={styles.casoTitle}>Cómo una empresa pasó de 7 a 17 ventas en el mes</h2>

          <p style={styles.casoText}>
            En julio una empresa de piscinas en Córdoba esperaba vender 7 piletas, lo normal para uno de los meses más frío del año. Tenía consultas entrando por WhatsApp e Instagram pero se le escapaban: mensajes sin responder fuera de horario, seguimientos olvidados, cero control de qué vendedor atendía a quién.
          </p>
          <p style={styles.casoText}>
            Ese mes cerró 17 ventas. No cambió la publicidad, no contrató más gente. Ordenó lo que ya tenía (lo que vos también podés hacer).
          </p>

          <div style={styles.casoFotoContainer}>
            <img
              src="/bivisual-team.jpg"
              alt="Leandro y Agustina — BiVisuaL Estudio"
              style={styles.casoFoto}
            />
          </div>

          <p style={styles.casoText}>
            Somos Leandro y Agustina, de BiVisuaL Estudio. Leandro arrancó a los 18 en una fábrica metalúrgica donde estuvo 5 meses sin cobrar. Ahí aprendió que el problema más caro de una empresa es no vender. Agustina viene del diseño y los medios, donde veía empresas con imagen impecable que no vendían lo que deberían. Nos unimos con una sola idea: que el contenido y la tecnología no sean ruido, sino un puente directo hacia la venta.
          </p>
          <p style={styles.casoText}>
            Lo que vas a ver ahora son datos de ejemplo que replican ese proceso. Tocá todo, explorá. Es tu sistema de prueba.
          </p>

          
           <a href="https://www.instagram.com/stories/highlights/18233432566317269/"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.casoHistoriaLink}
          >
            📍 Conocé más de nuestra historia
          </a>

          <div style={styles.welcomeButtons}>
            <button onClick={iniciarTour} style={styles.btnPrimary}>
              Ver el sistema →
            </button>
            <button onClick={saltarTour} style={styles.btnSkip}>
              Saltar
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // OVERLAY DEL TOUR (paso a paso)
  // ============================================
  if (!visible) return null;

  const paso = TOUR_STEPS[pasoActual];
  const esPrimero = pasoActual === 0;
  const esUltimo = pasoActual === TOUR_STEPS.length - 1;

  return (
    <div style={styles.tourBar}>
      <div style={styles.tourContent}>
        {/* Icono + Info */}
        <div style={styles.tourLeft}>
          <span style={styles.tourIcon}>{paso.icon}</span>
          <div>
            <div style={styles.tourStepCount}>
              Paso {pasoActual + 1} de {TOUR_STEPS.length}
            </div>
            <h3 style={styles.tourTitle}>{paso.titulo}</h3>
            <p style={styles.tourDesc}>{paso.descripcion}</p>
          </div>
        </div>

        {/* Botones */}
        <div style={styles.tourRight}>
          {!esPrimero && (
            <button onClick={pasoAnterior} style={styles.btnSecondary}>
              ← Anterior
            </button>
          )}
          <button onClick={esUltimo ? completarTour : siguientePaso} style={styles.btnPrimary}>
            {esUltimo ? '✓ Empezar a explorar' : 'Siguiente →'}
          </button>
          <button onClick={saltarTour} style={styles.btnClose} title="Cerrar tour">
            ✕
          </button>
        </div>
      </div>

      {/* Barra de progreso */}
      <div style={styles.progressBar}>
        <div style={{
          ...styles.progressFill,
          width: `${((pasoActual + 1) / TOUR_STEPS.length) * 100}%`
        }} />
      </div>
    </div>
  );
}

// ============================================
// ESTILOS INLINE (consistentes con el dark theme de BiLeads)
// ============================================
const styles = {
  // Overlay para bienvenida
  overlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    backdropFilter: 'blur(4px)',
  },

  // Card de bienvenida
  welcomeCard: {
    background: '#1a1a18',
    border: '1px solid #2a2a28',
    borderRadius: '16px',
    padding: '3rem',
    maxWidth: '480px',
    textAlign: 'center',
    animation: 'fadeIn 0.3s ease',
  },
  welcomeIcon: {
    fontSize: '3rem',
    marginBottom: '1rem',
  },
  welcomeTitle: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#e0e0e0',
    margin: '0 0 1rem 0',
  },
  welcomeText: {
    fontSize: '1rem',
    color: '#b0b0b0',
    lineHeight: '1.6',
    margin: '0 0 0.5rem 0',
  },
  welcomeSubtext: {
    fontSize: '0.875rem',
    color: '#888',
    margin: '0 0 2rem 0',
  },
  welcomeButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },

  // Card caso éxito
  casoCard: {
    background: '#1a1a18',
    border: '1px solid #2a2a28',
    borderRadius: '16px',
    padding: '2.5rem',
    maxWidth: '540px',
    maxHeight: '85vh',
    overflowY: 'auto',
    textAlign: 'center',
    animation: 'fadeIn 0.3s ease',
  },
  casoTitle: {
    fontSize: '1.35rem',
    fontWeight: 700,
    color: '#e0e0e0',
    margin: '0 0 1.5rem 0',
    lineHeight: '1.3',
  },
  casoText: {
    fontSize: '0.925rem',
    color: '#b0b0b0',
    lineHeight: '1.65',
    margin: '0 0 1rem 0',
    textAlign: 'left',
  },
  casoFotoContainer: {
    display: 'flex',
    justifyContent: 'center',
    margin: '1.25rem 0',
  },
  casoFoto: {
    width: '110px',
    height: '110px',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '2px solid #00F6ED',
  },
  casoHistoriaLink: {
    display: 'inline-block',
    fontSize: '0.85rem',
    color: '#00F6ED',
    textDecoration: 'none',
    marginBottom: '1.5rem',
    fontWeight: 500,
  },

  // Barra del tour (fija en la parte inferior)
  tourBar: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    background: '#1a1a18',
    borderTop: '1px solid #00F6ED',
    zIndex: 10000,
    boxShadow: '0 -4px 20px rgba(0, 246, 237, 0.15)',
  },
  tourContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1rem 2rem',
    maxWidth: '1400px',
    margin: '0 auto',
    gap: '2rem',
  },
  tourLeft: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '1rem',
    flex: 1,
  },
  tourIcon: {
    fontSize: '2rem',
    flexShrink: 0,
    marginTop: '0.25rem',
  },
  tourStepCount: {
    fontSize: '0.75rem',
    color: '#00F6ED',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '0.25rem',
  },
  tourTitle: {
    fontSize: '1.1rem',
    fontWeight: 700,
    color: '#e0e0e0',
    margin: '0 0 0.25rem 0',
  },
  tourDesc: {
    fontSize: '0.875rem',
    color: '#b0b0b0',
    margin: 0,
    lineHeight: '1.4',
    maxWidth: '600px',
  },
  tourRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flexShrink: 0,
  },

  // Barra de progreso
  progressBar: {
    height: '3px',
    background: '#2a2a28',
  },
  progressFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #00F6ED, #c12139)',
    transition: 'width 0.4s ease',
    borderRadius: '0 3px 3px 0',
  },

  // Botones
  btnPrimary: {
    padding: '0.625rem 1.25rem',
    background: 'linear-gradient(135deg, #00F6ED, #00d4cc)',
    border: 'none',
    borderRadius: '8px',
    color: '#060704',
    fontWeight: 700,
    fontSize: '0.875rem',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  btnSecondary: {
    padding: '0.625rem 1.25rem',
    background: 'transparent',
    border: '1px solid #3a3a38',
    borderRadius: '8px',
    color: '#e0e0e0',
    fontWeight: 600,
    fontSize: '0.875rem',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  btnSkip: {
    padding: '0.625rem 1.25rem',
    background: 'transparent',
    border: 'none',
    color: '#888',
    fontWeight: 500,
    fontSize: '0.875rem',
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  btnClose: {
    width: '32px',
    height: '32px',
    background: 'transparent',
    border: '1px solid #3a3a38',
    borderRadius: '6px',
    color: '#888',
    fontSize: '1rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
};
